import { inject, observer } from "mobx-react";
import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import {
    CreditApplication,
    CreditApplicationState,
    CreditApplicationStateFriendlyName,
    Customer,
    ExtractFriendlyName,
    InsuranceRecord,
    PermanentCreditApplication
} from "src/model/Customer";
import { InsuranceRecordRequest } from "src/requests/CustomerRequests";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { CustomerStore } from "src/stores/CustomerStore";

import { faCheck, faExclamationTriangle, faExternalLinkSquareAlt, faGavel, faTrash, faTrophy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HammerFallOutcome, HorseForSale, HorseForSalePurchase, HorseForSaleStateFriendlyName, SimpleHorseForSale } from "../../model/HorseForSale";
import { CustomerLink } from "../common/CustomerLink";
import { FinalBidAmountWarning } from "../common/FinalBidAmountWarning";
import { FormDialog } from "../common/FormDialog";
import { HorseLink } from "../common/HorseLink";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { TextareaGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";
import { CheckoutHorseHeader } from "./CheckoutHorsesHeader";
import CheckoutInsuranceComponent from "./CheckoutInsuranceComponent";
import { CheckoutTabs, EditableHorseForSalePurchase } from "./CheckoutTemplate";
import { SortFn } from "./CommonTableSorting";
import { CustomerLinkCheckout } from "../common/CustomerLinkCheckout";

interface RelevantCreditParts {
    purchaser: Customer;
    // Only one or the other, or none
    creditApplication: CreditApplication | null;
    onTheSpotCreditApplication: {
        notes: string;
    } | null;
    // Only specified iff a credit application exists
    insuranceRecord: InsuranceRecord | null;
}

const CreditPartsFromWinningBidder = (fields: EditableHorseForSalePurchase.Fields, winningBidder: Customer): RelevantCreditParts => {
    return {
        purchaser: winningBidder,
        creditApplication: fields.winningBidderCreditApplication,
        onTheSpotCreditApplication: fields.winningBidderOnTheSpotCreditApplication,
        insuranceRecord: fields.winningBidderInsuranceRecord
    };
};

export interface Props {
    configurationStore?: ConfigurationStore;
    customerStore?: CustomerStore;

    winningBidder: Customer;
    currentHorseForSale: HorseForSale;

    //Form Action
    onChange: (currentHorseForSale: HorseForSale, purchasers: EditableHorseForSalePurchase.Fields) => void;
    horseForSalePurchaseList: EditableHorseForSalePurchase.Fields;
    onCustomerUpdated: (customer: Customer) => any;

    goToTab: (currentTab: string) => void;
}

export interface State {
    // TODO: Group these according to how they are used

    currentlyEditing: RelevantCreditParts | null;

    showGrantDialog: boolean;
    currentHorseForSalePurchaseNotes: string;
    // currentHorseForSalePurchaseInsurance: InsuranceRecord | null;
    wasValidated: boolean;
    showInsuranceRecordDialog: boolean;
    wasValidatedInsurance: boolean;
    overridenInsurance: Array<number>;
}

@inject("customerStore")
@inject("configurationStore")
@observer
export default class CheckoutCreditAndInsurance extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            wasValidated: false,
            showGrantDialog: false,
            currentlyEditing: null,
            currentHorseForSalePurchaseNotes: "",
            // currentHorseForSalePurchaseInsurance: null,
            showInsuranceRecordDialog: false,
            wasValidatedInsurance: false,
            overridenInsurance: []
        };
    }

    get isWinningBidderABuyer(): boolean {
        return this.props.horseForSalePurchaseList.buyers.some(hfsp => hfsp.purchaser!.id === this.props.winningBidder.id);
    }

    get currentHorseForSalePurchase(): EditableHorseForSalePurchase.PurchaseFields | null {
        const currentlyEditing = this.state.currentlyEditing;
        if (currentlyEditing === null) {
            return null;
        } else {
            return this.findHorseForSalePurchase(currentlyEditing);
        }
    }

    get currentHorseForSalePurchaseIsWinningBidder(): boolean {
        return (this.state.currentlyEditing && this.state.currentlyEditing.purchaser.id === this.props.winningBidder.id) || false;
    }

    findHorseForSalePurchase(relevantParts: RelevantCreditParts): EditableHorseForSalePurchase.PurchaseFields | null {
        return this.props.horseForSalePurchaseList.buyers.find(hfsp => hfsp.purchaser!.id === relevantParts.purchaser.id) || null;
    }

    isWinnerBidderAnAgentAuthorizationForThisPurchaser = (purchaser: Customer): boolean => {
        const winnerBidderId = this.props.winningBidder!.id;
        const currentHorseForSaleSaleId = this.props.currentHorseForSale.sale.id;

        return !!(
            purchaser.id !== winnerBidderId &&
            purchaser.delegatorAuthorizations.find(
                aa => aa.agent.id === winnerBidderId && aa.sale.id === currentHorseForSaleSaleId && aa.dateRevoked === null
            )
        );
    };

    purchaserActiveInsuranceRecords = (hfsp: RelevantCreditParts) => {
        return hfsp.purchaser.insuranceRecords.filter(ir => ir.dateCancelled === null);
    };

    creditApplicationCell = (hfsp: RelevantCreditParts) => {
        const activeCredit =
            hfsp.purchaser.creditApplications.find(c => c.type === "PERMANENT" && c.state === CreditApplicationState.APPROVED) || null;
        if (hfsp.creditApplication) {
            return "Yes";
        } else if (hfsp.onTheSpotCreditApplication) {
            return (
                <>
                    Yes
                    <button
                        className="btn btn-sm btn-secondary ml-2"
                        id={`tooltipUngrant_${hfsp.purchaser!.id}`}
                        onClick={() => this.undoGrantCredit(this.findHorseForSalePurchase(hfsp)!)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                        <UncontrolledTooltip placement="top" target={`tooltipUngrant_${hfsp.purchaser!.id}`} delay={0} className="tooltipstrap">
                            Undo credit for this purchase
                        </UncontrolledTooltip>
                    </button>
                </>
            );
        } else {
            return (
                <>
                    No
                    <button
                        className="btn btn-sm btn-primary d-block mt-2"
                        onClick={() => {
                            if (activeCredit) {
                                this.fillInMissingPermanentCredit(hfsp, activeCredit);
                            } else {
                                this.setState({
                                    showGrantDialog: true,
                                    currentlyEditing: hfsp
                                });
                            }
                        }}
                    >
                        {activeCredit ? "Use Existing Credit" : "Grant Credit"}
                    </button>
                </>
            );
        }
    };

    creditNotesCell = (hfsp: RelevantCreditParts) => {
        if (hfsp.creditApplication) {
            return hfsp.creditApplication.notes;
        } else if (hfsp.onTheSpotCreditApplication) {
            return hfsp.onTheSpotCreditApplication.notes;
        } else {
            return "N/A";
        }
    };

    creditStateCell = (hfsp: RelevantCreditParts) => {
        if (hfsp.creditApplication) {
            return (
                <span className={`${hfsp.creditApplication.state !== CreditApplicationState.APPROVED ? "text-danger" : "text-success"}`}>
                    {CreditApplicationStateFriendlyName(hfsp.creditApplication.state)}
                </span>
            );
        } else if (hfsp.onTheSpotCreditApplication) {
            return "Approved for this Purchase";
        } else {
            return "N/A";
        }
    };

    grantCredit = async () => {
        const purchaser = this.state.currentlyEditing!.purchaser;
        if (!this.currentHorseForSalePurchaseIsWinningBidder && !this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(purchaser)) {
            const name = ExtractFriendlyName(purchaser);
            const goAhead = await SimpleConfirmation(
                `${name} 
                hasn't authorized ${ExtractFriendlyName(this.props.winningBidder)} to act as an agent. 
                Are you sure you'd like to grant credit to ${name}?`
            );
            if (!goAhead) {
                return;
            }
        }

        if (this.currentHorseForSalePurchaseIsWinningBidder) {
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderOnTheSpotCreditApplication: {
                    notes: this.state.currentHorseForSalePurchaseNotes || ""
                }
            });
        } else {
            // We're modifying a props object directly .... that's a bad practice. Should we do a deep copy?
            const hfsp = this.currentHorseForSalePurchase!;
            hfsp.onTheSpotCreditApplication = {
                notes: this.state.currentHorseForSalePurchaseNotes || ""
            };

            this.replaceHorseForSalePurchase(hfsp);
        }

        this.closeGrantDialog();
    };

    fillInMissingPermanentCredit = (creditParts: RelevantCreditParts, creditApplication: CreditApplication) => {
        const hfsp = this.findHorseForSalePurchase(creditParts)!;
        if (this.props.winningBidder.id === creditParts.purchaser.id) {
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderOnTheSpotCreditApplication: null,
                winningBidderCreditApplication: creditApplication
            });
        } else {
            // We're modifying a props object directly .... that's a bad practice. Should we do a deep copy?
            hfsp.onTheSpotCreditApplication = null;
            hfsp.creditApplication = creditApplication;

            this.replaceHorseForSalePurchase(hfsp);
        }
    };

    insuranceCompanyCell = (hfsp: RelevantCreditParts) => {
        return (
            <select
                className="form-control not-required"
                name="insurance"
                value={hfsp.insuranceRecord ? hfsp.insuranceRecord.id : ""}
                disabled={
                    (hfsp.creditApplication === null && hfsp.onTheSpotCreditApplication === null) ||
                    this.state.overridenInsurance.some(o => o === hfsp.purchaser!.id)
                }
                required={hfsp.creditApplication !== null || hfsp.onTheSpotCreditApplication !== null}
                onChange={e => {
                    if (e.target.value === "NEW") {
                        this.setState({
                            currentlyEditing: hfsp,
                            showInsuranceRecordDialog: true
                        });
                    } else {
                        const value = e.target.value;
                        this.onInsuranceRecordSelected(hfsp, value === "" ? null : parseInt(value, 10));
                    }
                }}
            >
                <option value=""> None </option>
                {this.purchaserActiveInsuranceRecords(hfsp).map(ir => (
                    <option key={ir.id} value={ir.id}>
                        {ir.company.businessName}{" "}
                    </option>
                ))}
                <option value="NEW">Add New Insurance Record</option>
            </select>
        );
    };

    insuranceOverrideCell = (hfsp: RelevantCreditParts) => {
        const currentlyOverridden = this.state.overridenInsurance.some(id => id === hfsp.purchaser.id);
        const activeRecords = this.purchaserActiveInsuranceRecords(hfsp);
        if (activeRecords.length > 0) {
            return null;
        } else if (currentlyOverridden) {
            return (
                <button
                    onClick={() => {
                        this.setState({ overridenInsurance: this.state.overridenInsurance.filter(id => id !== hfsp.purchaser.id) });
                    }}
                    id={`tooltipDelete${hfsp.purchaser!.id}`}
                    className="btn btn-warning btn-sm"
                >
                    Revoke Insurance Override
                </button>
            );
        } else {
            return (
                <button
                    onClick={() => {
                        this.setState({ overridenInsurance: [...this.state.overridenInsurance, hfsp.purchaser.id] });
                        successMessage(`The insurance requirement for selected horse for sale has been overridden.`);
                    }}
                    id={`tooltipDelete${hfsp.purchaser!.id}`}
                    className="btn btn-primary btn-sm"
                    disabled={hfsp.creditApplication === null && hfsp.onTheSpotCreditApplication === null}
                >
                    Override Insurance Requirement
                </button>
            );
        }
    };

    undoGrantCredit = (hfsp: EditableHorseForSalePurchase.PurchaseFields) => {
        if (hfsp.purchaser!.id === this.props.winningBidder.id) {
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderOnTheSpotCreditApplication: null
            });
        } else {
            hfsp.onTheSpotCreditApplication = null;
            this.replaceHorseForSalePurchase(hfsp);
        }
    };

    closeGrantDialog = () => {
        this.setState({
            showGrantDialog: false,
            currentHorseForSalePurchaseNotes: "",
            wasValidated: false
        });
    };

    submitGrantCreditDialog = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.form && this.form.checkValidity() && this.state.currentHorseForSalePurchaseNotes !== "") {
            this.setState({ wasValidated: false });
            this.grantCredit();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    closeInsuranceDialog = () => {
        this.setState({
            showInsuranceRecordDialog: false,
            currentlyEditing: null,
            currentHorseForSalePurchaseNotes: "",
            wasValidatedInsurance: false
        });
    };

    onInsuranceRecordSelected = (creditParts: RelevantCreditParts, insuranceRecordId: number | null) => {
        const winningBidder = this.props.winningBidder;
        if (creditParts.purchaser.id === winningBidder.id) {
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderInsuranceRecord: winningBidder.insuranceRecords.find(ir => ir.id === insuranceRecordId) || null
            });
        } else {
            const hfsp = this.findHorseForSalePurchase(creditParts)!;
            // We're modifying a props object directly .... that's a bad practice. Should we do a deep copy?
            hfsp.insuranceRecord = hfsp.purchaser!.insuranceRecords.find(ir => ir.id === insuranceRecordId) || null;

            this.replaceHorseForSalePurchase(hfsp);
        }
    };

    onInsuranceRecordCreated = async (updatedCustomer: Customer, record: InsuranceRecord) => {
        if (this.state.currentlyEditing === null) {
            throw new Error("In onInsuranceRecordCreated with a null currentlyEditing");
        }
        this.props.onCustomerUpdated(updatedCustomer);

        // We repeat similar logic to onInsuranceRecordSelected so we can use `record` directly
        // (Since the new insurance record won't be available yet in this.props)
        if (this.currentHorseForSalePurchaseIsWinningBidder) {
            this.props.onChange(this.props.currentHorseForSale, {
                ...this.props.horseForSalePurchaseList,
                winningBidderInsuranceRecord: record
            });
        } else {
            const hfsp = this.currentHorseForSalePurchase;
            // We're modifying a props object directly .... that's a bad practice. Should we do a deep copy?
            if (!hfsp) {
                throw new Error("Could not find active horse for sale in onInsuranceRecordCreated");
            }
            hfsp.insuranceRecord = record;
            this.replaceHorseForSalePurchase(hfsp);
        }

        this.closeInsuranceDialog();
    };

    submit = () => {
        this.props.onChange(this.props.currentHorseForSale, this.props.horseForSalePurchaseList);
        this.props.goToTab(CheckoutTabs.HST_EXEMPTION);
    };

    readyToProceed = (): boolean => {
        // Make sure everything with credit either has an insurance company or is overridden explicitly
        return (
            // For Agents:
            this.props.horseForSalePurchaseList.buyers
                // First, filter the list so that we only have the winning bidder and anyone who is agent authorized
                .filter(hfsp => this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(hfsp.purchaser!))
                // Then, filter to only those with a credit application
                .filter(hfsp => hfsp.onTheSpotCreditApplication || hfsp.creditApplication)
                // We're good to go if all of the remaining purchasers have an insurance record, or that requirement has been overridden
                .every(hfsp => hfsp.insuranceRecord !== null || this.state.overridenInsurance.some(id => id === hfsp.purchaser!.id)) &&
            // For the winning bidder
            (this.props.horseForSalePurchaseList.winningBidderCreditApplication ||
            this.props.horseForSalePurchaseList.winningBidderOnTheSpotCreditApplication
                ? this.props.horseForSalePurchaseList.winningBidderInsuranceRecord !== null ||
                  this.state.overridenInsurance.some(id => id === this.props.winningBidder.id)
                : true)
        );
    };

    winningBidderRow() {
        const winningBidder = this.props.winningBidder;
        const creditParts = CreditPartsFromWinningBidder(this.props.horseForSalePurchaseList, winningBidder);
        // Including the winning bidder is unnecessary if everyone has agent authorization
        if (this.props.horseForSalePurchaseList.buyers.every(b => this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(b.purchaser!))) {
            return null;
        }

        const classNames = "table-bordered-td-bg text-info";

        return (
            <tr>
                <td className={classNames}>
                    <CustomerLinkCheckout page="credit-application" customer={winningBidder} isWinningBider={true} />
                </td>
                <td className={classNames}>{this.creditApplicationCell(creditParts)}</td>
                <td className={classNames}>{this.creditNotesCell(creditParts)}</td>
                <td className={classNames}>{this.creditStateCell(creditParts)}</td>
                <td className={classNames} />
                <td className={classNames}>{this.insuranceCompanyCell(creditParts)}</td>
                <td className={classNames}>{this.insuranceOverrideCell(creditParts)}</td>
            </tr>
        );
    }

    allOtherRows() {
        const winningBidder = this.props.winningBidder;
        const horseForSalePurchaseList = this.props.horseForSalePurchaseList.buyers
            .filter(b => b.purchaser!.id !== winningBidder.id)
            .sort(SortFn(this.props.winningBidder, this.props.currentHorseForSale.sale.id));

        return horseForSalePurchaseList.map(rawRequest => {
            const hfsp = { ...rawRequest, purchaser: rawRequest.purchaser! };
            const hasAgentAuth = this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(hfsp.purchaser);
            const classNames = !hasAgentAuth ? "table-bordered-td-bg" : "";
            return (
                <tr key={hfsp.purchaser!.id}>
                    <td className={classNames}>
                        <div className="d-flex justify-content-start align-items-center">
                            <CustomerLinkCheckout page="credit-application" customer={hfsp.purchaser!} notHaveAgentAuthorization={!hasAgentAuth} />
                        </div>
                    </td>
                    <td className={classNames}>{hasAgentAuth ? this.creditApplicationCell(hfsp) : ""}</td>
                    <td className={classNames}>{hasAgentAuth ? this.creditNotesCell(hfsp) : ""}</td>
                    <td className={classNames}>{hasAgentAuth ? this.creditStateCell(hfsp) : ""}</td>
                    <td className={classNames}>{hfsp.trainer === true ? "Trainer" : ""}</td>
                    <td className={classNames}>{hasAgentAuth ? this.insuranceCompanyCell(hfsp) : ""}</td>
                    <td className={classNames}>{hasAgentAuth ? this.insuranceOverrideCell(hfsp) : ""}</td>
                </tr>
            );
        });
    }

    render() {
        const horseForSale = this.props.currentHorseForSale;
        const finalBidAmount = (horseForSale.outcome as HammerFallOutcome).finalBidAmount / 100;
        return (
            <>
                <div className="card shadow">
                    <div className="card-header">
                        <CheckoutHorseHeader stepNumber={2} stepTitle="Credit and Insurance" currentHorseForSale={this.props.currentHorseForSale} />
                    </div>
                    <div className="card-body">
                        <FinalBidAmountWarning finalBidAmount={finalBidAmount} />
                        <div className="form-row my-3">
                            <div className="col-12">
                                <div className="table-responsive">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: "230px" }}>Winning Bidder & Buyer(s)</th>
                                                <th>Credit Applied</th>
                                                <th>Credit Notes</th>
                                                <th>Credit Status</th>
                                                <th>Additional Info</th>
                                                <th>Insurance Company</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.winningBidderRow()}
                                            {this.allOtherRows()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="for-row text-right">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.props.goToTab(CheckoutTabs.BUYERS);
                                }}
                            >
                                Back to Buyers
                            </button>
                            <button
                                form="agentAuthorization"
                                className="btn btn-primary btn-sm mr-0 ml-3"
                                disabled={!this.readyToProceed()}
                                onClick={() => this.submit()}
                            >
                                Go to HST Exemption
                            </button>
                        </div>
                    </div>
                </div>
                <FormDialog title="Grant Credit" modalSize="modal-md" show={this.state.showGrantDialog} onClose={() => this.closeGrantDialog()}>
                    <form
                        className={"px-2 needs-validation " + (this.state.wasValidated === true ? "was-validated" : "")}
                        noValidate={true}
                        onSubmit={e => this.submitGrantCreditDialog(e)}
                        ref={ref => (this.form = ref)}
                        id="invoiceForm"
                    >
                        <div className="form-row">
                            <TextareaGroup
                                label="Notes"
                                rows={2}
                                isRequired={true}
                                name="notes"
                                className="form-group col-12"
                                value={this.state.currentHorseForSalePurchaseNotes || ""}
                                onChange={e => this.setState({ currentHorseForSalePurchaseNotes: e.target.value })}
                            />
                        </div>
                        <div className="form-row text-right mt-4">
                            <button type="button" className="btn btn-secondary mr-auto" onClick={() => this.closeGrantDialog()}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" form="invoiceForm">
                                Grant Credit for this purchase
                            </button>
                        </div>
                    </form>
                </FormDialog>

                <CheckoutInsuranceComponent
                    showInsuranceRecordDialog={this.state.showInsuranceRecordDialog}
                    closeInsuranceDialog={() => this.closeInsuranceDialog()}
                    onInsuranceRecordCreated={this.onInsuranceRecordCreated}
                    currentCustomer={this.state.currentlyEditing ? this.state.currentlyEditing.purchaser : null}
                />
            </>
        );
    }

    private replaceHorseForSalePurchase(hfsp: EditableHorseForSalePurchase.PurchaseFields) {
        const filteredHorseForSalePurchase = this.props.horseForSalePurchaseList.buyers.filter(p => p.purchaser!.id !== hfsp.purchaser!.id);
        this.props.onChange(this.props.currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: [...filteredHorseForSalePurchase, hfsp]
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutCreditAndInsurance.tsx